import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Fish, Anchor, Waves, ShieldCheck, Check, BarChart3, ExternalLink, Loader2, Map } from "lucide-react";
import api from "@/lib/api";
import { findDashboardByKode, embedUrl, viewUrl } from "@/lib/dashboards";

const iconMap = { Fish, Anchor, Waves, ShieldCheck };

export default function BidangDetail() {
  const { slug } = useParams();
  const [b, setB] = useState(null);
  const [err, setErr] = useState("");
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [activeGisIndex, setActiveGisIndex] = useState(null);
  const [gisLoaded, setGisLoaded] = useState(false);

  useEffect(() => {
    setB(null); setErr("");
    api.get(`/bidang/${slug}`).then((r) => setB(r.data)).catch(() => setErr("Bidang tidak ditemukan"));
  }, [slug]);

  useEffect(() => {
    setIframeLoaded(false);
    setActiveGisIndex(null);
    setGisLoaded(false);
  }, [slug]);

  if (err) return <div className="max-w-3xl mx-auto py-24 px-4 text-center text-[#003A5C]">{err}</div>;
  if (!b) return <div className="max-w-3xl mx-auto py-24 px-4 text-center text-[#003A5C]">Memuat...</div>;

  const Icon = iconMap[b.icon] || Fish;
  const dash = findDashboardByKode(b.kode);

  return (
    <div className="min-h-[70vh]">
      <div className="relative h-[45vh] overflow-hidden">
        <img src={b.image} alt={b.nama} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#003A5C]/95 via-[#003A5C]/70 to-transparent" />
        <div className="relative max-w-5xl mx-auto px-4 h-full flex flex-col justify-end pb-10">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-4"><ArrowLeft className="w-4 h-4" /> Kembali</Link>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#48CAE4] font-bold mb-2">
            <Icon className="w-4 h-4" /> {b.kode}
          </div>
          <h1 className="font-heading font-bold text-white text-3xl lg:text-5xl leading-tight max-w-3xl">{b.nama}</h1>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-4 py-14 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <h2 className="font-heading font-bold text-2xl text-[#003A5C] mb-4">Tentang Bidang</h2>
          <p className="text-slate-700 leading-relaxed">{b.deskripsi}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 shadow-md p-6">
          <div className="text-xs uppercase tracking-widest text-[#0086C6] font-semibold mb-4">Fokus Kerja</div>
          <ul className="space-y-3">
            {b.fokus.map((f, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#E1F5FF] grid place-items-center shrink-0 mt-0.5">
                  <Check className="w-3.5 h-3.5 text-[#005F8E]" />
                </div>
                <span className="text-sm text-slate-700">{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {dash && (
        <div className="max-w-7xl mx-auto px-4 pb-16">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#0086C6] font-semibold mb-2">
                <BarChart3 className="w-4 h-4" /> Dashboard Interaktif
              </div>
              <h2 className="font-heading font-bold text-2xl lg:text-3xl text-[#003A5C]">Statistik {dash.label}</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {dash.gisMaps?.map((g, i) => (
                <button key={g.label} onClick={() => { setGisLoaded(false); setActiveGisIndex((cur) => (cur === i ? null : i)); }}
                   className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 font-bold text-sm transition-colors ${
                     activeGisIndex === i
                       ? "bg-[#005F8E] text-white border-[#005F8E]"
                       : "border-[#005F8E] text-[#005F8E] hover:bg-[#005F8E] hover:text-white"
                   }`}>
                  <Map className="w-3.5 h-3.5" /> {g.label}
                </button>
              ))}
              <Link to={`/statistik/${dash.slug}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-[#005F8E] text-[#005F8E] hover:bg-[#005F8E] hover:text-white font-bold text-sm transition-colors">
                Halaman Statistik
              </Link>
              <a href={viewUrl(dash)} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00B4D8] hover:bg-[#48CAE4] text-[#003A5C] font-bold text-sm transition-colors">
                Buka di Tab Baru <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          {activeGisIndex !== null && (() => {
            const g = dash.gisMaps[activeGisIndex];
            const openUrl = g.type === "arcgis-map" ? `https://www.arcgis.com/home/item.html?id=${g.itemId}` : g.url;
            return (
              <div className="bg-white rounded-3xl border border-slate-100 shadow-lg overflow-hidden mb-6">
                <div className="bg-[#003A5C] text-white px-6 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/10 grid place-items-center"><Map className="w-4 h-4" /></div>
                    <div>
                      <div className="font-heading font-bold text-sm">{g.label?.toUpperCase()}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#48CAE4]">Powered by ArcGIS</div>
                    </div>
                  </div>
                  <a href={openUrl} target="_blank" rel="noopener noreferrer" className="text-[#48CAE4] hover:text-white text-xs inline-flex items-center gap-1">
                    Buka di Tab Baru <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                {g.type === "arcgis-map" ? (
                  <arcgis-embedded-map
                    style={{ height: "600px", width: "100%" }}
                    item-id={g.itemId}
                    theme={g.theme}
                    time-zone-label-enabled=""
                    center={g.center}
                    scale={g.scale}
                    portal-url={g.portalUrl}
                  />
                ) : (
                  <div className="relative">
                    {!gisLoaded && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-50" style={{ height: "700px" }}>
                        <Loader2 className="w-8 h-8 text-[#005F8E] animate-spin" />
                        <div className="text-sm text-slate-500">Memuat peta...</div>
                      </div>
                    )}
                    <iframe
                      title={g.label}
                      src={g.url}
                      className="w-full bg-slate-50"
                      style={{ height: "700px", border: 0 }}
                      allowFullScreen
                      onLoad={() => setGisLoaded(true)}
                      data-testid={`bidang-gis-${dash.slug}`}
                    />
                  </div>
                )}
              </div>
            );
          })()}

          <div className="bg-white rounded-3xl border border-slate-100 shadow-lg overflow-hidden">
            <div className="bg-[#003A5C] text-white px-6 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/10 grid place-items-center"><BarChart3 className="w-4 h-4" /></div>
                <div>
                  <div className="font-heading font-bold text-sm">{dash.title.toUpperCase()}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#48CAE4]">Powered by Google Looker Studio</div>
                </div>
              </div>
            </div>
            <div className="relative">
              {!iframeLoaded && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-50" style={{ height: "900px" }}>
                  <Loader2 className="w-8 h-8 text-[#005F8E] animate-spin" />
                  <div className="text-sm text-slate-500">Memuat dashboard...</div>
                </div>
              )}
              <iframe
                title={dash.title}
                src={embedUrl(dash)}
                className="w-full bg-slate-50"
                style={{ height: "900px", border: 0 }}
                allowFullScreen
                onLoad={() => setIframeLoaded(true)}
                data-testid={`bidang-datastudio-${dash.slug}`}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
